import { ITranscription, ITranscriptions } from '@/types/task.interface'

// Seconds of already shown live text re-fetched on every poll, so server-side
// corrections (the listener replaces a minute of translation with a refined
// one) reach the page. 0 keeps the append-only polling.
export const LIVE_REFRESH_WINDOW_SECONDS = Number(
	import.meta.env.VITE_LIVE_REFRESH_WINDOW_SECONDS ?? 0
)

const hasChunkText = (chunk: ITranscription) =>
	Boolean(
		chunk.content.trim() ||
			chunk.segments.some(segment => segment.content.trim())
	)

/** Start of the earliest chunk inside the refresh window, or null when the
 *  window is off or nothing is loaded yet. Re-fetching from a chunk boundary
 *  keeps the server's chunk grid identical to the one already on screen. */
export const getRefreshBoundary = (
	data: ITranscriptions | null,
	lastStartAfter: number | null,
	windowSeconds = LIVE_REFRESH_WINDOW_SECONDS
): number | null => {
	if (!windowSeconds || !data || typeof lastStartAfter !== 'number') return null

	const chunk = data.transcriptions.original.find(
		item => item.start >= lastStartAfter - windowSeconds
	)

	return chunk ? chunk.start : null
}

/** Chunks starting at or after `boundary` are replaced by the server's version. */
export const replaceChunksFrom = (
	prevChunks: ITranscription[],
	nextChunks: ITranscription[],
	boundary: number
) =>
	[
		...prevChunks.filter(chunk => chunk.start < boundary),
		...nextChunks.filter(hasChunkText)
	].sort((a, b) => a.start - b.start)

export const replaceTranscriptionsFrom = (
	prev: ITranscriptions,
	next: ITranscriptions,
	boundary: number
): ITranscriptions => ({
	...next,
	transcriptions: {
		original: replaceChunksFrom(
			prev.transcriptions.original,
			next.transcriptions.original,
			boundary
		),
		translation: replaceChunksFrom(
			prev.transcriptions.translation,
			next.transcriptions.translation,
			boundary
		)
	}
})
